/* eslint-disable react/jsx-no-useless-fragment */
import { Box, ToggleButton, Typography } from '@mui/material';
import { Fragment, useEffect } from 'react';

import { ElementData } from '../../../api/samples/types';
import { WorkOrderData } from '../../../api/workOrders/types';
import { ExportSample } from '../../../components/ExportSample';
import ElementsMap from '../../../components/Maps/ElementsMap';
import { CardPagination } from '../../../components/Sections/Sample/CardPagination';
import { DashboardIcon } from '../../../components/Sections/Sample/DashboardIcon';
import { UpButton } from '../../../components/Sections/Sample/UpButton';
import { ColoredCircularProgress } from '../../../components/UI/Typography';
import { inferencesTypes } from '../../../constants/evaluationTypes';
import {
  IconAddMS,
  IconViewArrayMS,
  IconViewListMS,
} from '../../../constants/icons';
import { Constants } from '../../../constants/sampleCreation';
import { privateUnitsGroup } from '../../../constants/selectOptions';
import useSampleCreation from '../useSampleCreation';
import { ElementCard } from './ElementCard';
import {
  DashboardContainer,
  FlexBox,
  LevelButton,
  MissingBox,
  SampleText,
  SectionBox,
  StyledDivider,
  StyledToggleButtonGroup,
  StyledTooltip,
} from './styles';

type LatLngLiteral = google.maps.LatLngLiteral;

interface SampleProps {
  propertyData: WorkOrderData;
  sampleData: ElementData[];
  setSampleData: (sample: ElementData[]) => void;
  completeElements: ElementData[];
  incompleteElements: ElementData[];
  pinPlace: LatLngLiteral | undefined;
  showMap: boolean;
  updateSample: () => Promise<void>;
  minElements: string;
  disapprovedElements: number[];
  setDisapprovedElements: (value: number[]) => void;
  page: number;
  setPage: (value: number) => void;
  setLastElementEdited: (value: number | undefined) => void;
}

export function Sample({
  propertyData,
  sampleData,
  setSampleData,
  completeElements,
  incompleteElements,
  pinPlace,
  showMap,
  updateSample,
  minElements,
  disapprovedElements,
  setDisapprovedElements,
  page,
  setPage,
  setLastElementEdited,
}: SampleProps): JSX.Element {
  const {
    view,
    handleView,
    complete,
    setComplete,
    incomplete,
    setIncomplete,
    deactivateFilters,
    osId,
  } = useSampleCreation();

  useEffect(() => {
    if (view === 'list') document.getElementById(`${page}`)?.scrollIntoView();
  }, [page, view]);

  const handleLastEditedPage = (): void => setLastElementEdited(page);

  const pageValue = (element?: ElementData): number => {
    if (element) {
      return sampleData.indexOf(element) + 1;
    }
    if (complete) {
      return sampleData.indexOf(completeElements[page - 1]) + 1;
    }
    if (incomplete) {
      return sampleData.indexOf(incompleteElements[page - 1]) + 1;
    }
    return page;
  };

  const handleMinElements = (): string => {
    if (inferencesTypes.includes(propertyData.evaluation_type)) {
      if (privateUnitsGroup.has(propertyData.real_estate_kind)) {
        return Constants.minInfeApartment;
      }
      return Constants.minInfeHouse;
    }
    return Constants.minFactors;
  };

  return (
    <>
      {showMap && (
        <SampleText>
          {`A amostra possui ${sampleData.length} elementos`}
        </SampleText>
      )}
      {sampleData.length > 0 && (
        <>
          {pinPlace &&
            (showMap ? (
              <ElementsMap
                sampleData={sampleData}
                pinPlace={pinPlace}
                setPage={setPage}
                page={pageValue()}
                deactivateFilters={deactivateFilters}
                sampleCreationStatus
              />
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  padding: 8,
                }}
              >
                <ColoredCircularProgress />
              </Box>
            ))}
          <SectionBox id="top" sx={{ justifyContent: 'space-between' }}>
            <StyledToggleButtonGroup
              value={view}
              exclusive
              onChange={handleView}
              aria-label="visualização"
            >
              <ToggleButton value="card" aria-label="individual">
                {IconViewArrayMS}
              </ToggleButton>
              <ToggleButton value="list" aria-label="lista">
                {IconViewListMS}
              </ToggleButton>
            </StyledToggleButtonGroup>
            <StyledDivider orientation="vertical" flexItem />
            <FlexBox>
              <Box>
                <LevelButton
                  complete="true"
                  value="complete"
                  selected={complete}
                  onChange={() => {
                    setPage(1);
                    setIncomplete(false);
                    setComplete(!complete);
                  }}
                >
                  {Constants.complete}
                </LevelButton>
                <LevelButton
                  value="incomplete"
                  selected={incomplete}
                  onChange={() => {
                    setPage(1);
                    setComplete(false);
                    setIncomplete(!incomplete);
                  }}
                >
                  {Constants.incomplete}
                </LevelButton>
              </Box>
              <ExportSample
                responsive
                osId={osId}
                title={Constants.exportButtonTitle}
              />
            </FlexBox>
          </SectionBox>
          <Typography fontSize={18}>{handleMinElements()}</Typography>
          {!complete && !incomplete && (
            <>
              <DashboardContainer>
                {sampleData.map((element, ind) => (
                  <Fragment key={element.id}>
                    {element.description.level === 1 && element.approved ? (
                      <DashboardIcon
                        page={ind + 1}
                        setPage={setPage}
                        sampleId={ind + 1}
                        alert={false}
                      />
                    ) : (
                      <DashboardIcon
                        page={ind + 1}
                        setPage={setPage}
                        sampleId={ind + 1}
                        alert
                      />
                    )}
                  </Fragment>
                ))}
                {minElements && (
                  <StyledTooltip title="máximo de elementos" placement="top">
                    <MissingBox>
                      {minElements}
                      {IconAddMS}
                    </MissingBox>
                  </StyledTooltip>
                )}
              </DashboardContainer>
              {view === 'card' ? (
                <>
                  <CardPagination
                    sampleCreationPage
                    sampleLength={sampleData.length}
                    page={page}
                    setPage={setPage}
                  />
                  <ElementCard
                    sampleData={sampleData}
                    setSampleData={setSampleData}
                    updateSample={updateSample}
                    disapprovedElements={disapprovedElements}
                    setDisapprovedElements={setDisapprovedElements}
                    sampleId={pageValue()}
                    elementData={sampleData[page - 1]}
                    propertyData={propertyData}
                    handleLastEditedPage={handleLastEditedPage}
                    page={page}
                    setPage={setPage}
                    goToLastPage={
                      sampleData.length > 1 && sampleData.length === page
                    }
                  />
                </>
              ) : (
                <Box>
                  {sampleData.map((element, idx) => (
                    <Box id={`${idx + 1}`} key={element.id}>
                      <ElementCard
                        sampleData={sampleData}
                        setSampleData={setSampleData}
                        updateSample={updateSample}
                        disapprovedElements={disapprovedElements}
                        setDisapprovedElements={setDisapprovedElements}
                        sampleId={pageValue(element)}
                        elementData={element}
                        propertyData={propertyData}
                        handleLastEditedPage={handleLastEditedPage}
                        page={page}
                        setPage={setPage}
                        goToLastPage={sampleData.length > 1}
                      />
                    </Box>
                  ))}
                  <UpButton />
                </Box>
              )}
            </>
          )}
          {complete && (
            <>
              <DashboardContainer>
                {completeElements?.map((element, ind) => (
                  <DashboardIcon
                    key={element.id}
                    page={ind + 1}
                    setPage={setPage}
                    sampleId={sampleData.indexOf(element) + 1}
                    alert={false}
                  />
                ))}
                {minElements && (
                  <StyledTooltip title="máximo de elementos" placement="top">
                    <MissingBox>
                      {minElements}
                      {IconAddMS}
                    </MissingBox>
                  </StyledTooltip>
                )}
              </DashboardContainer>
              {completeElements.length > 0 && (
                <>
                  {view === 'card' ? (
                    <>
                      <CardPagination
                        sampleCreationPage
                        sampleLength={completeElements.length}
                        page={page}
                        setPage={setPage}
                      />
                      <ElementCard
                        sampleData={sampleData}
                        setSampleData={setSampleData}
                        updateSample={updateSample}
                        disapprovedElements={disapprovedElements}
                        setDisapprovedElements={setDisapprovedElements}
                        sampleId={pageValue()}
                        elementData={completeElements[page - 1]}
                        propertyData={propertyData}
                        handleLastEditedPage={handleLastEditedPage}
                        page={page}
                        setPage={setPage}
                        goToLastPage={
                          completeElements.length > 1 &&
                          completeElements.length === page
                        }
                      />
                    </>
                  ) : (
                    <>
                      <Box>
                        {completeElements.map((element, idx) => (
                          <Box id={`${idx + 1}`} key={element.id}>
                            <ElementCard
                              sampleData={sampleData}
                              setSampleData={setSampleData}
                              updateSample={updateSample}
                              disapprovedElements={disapprovedElements}
                              setDisapprovedElements={setDisapprovedElements}
                              sampleId={pageValue(element)}
                              elementData={element}
                              propertyData={propertyData}
                              handleLastEditedPage={handleLastEditedPage}
                              page={page}
                              setPage={setPage}
                              goToLastPage={sampleData.length > 1}
                            />
                          </Box>
                        ))}
                        <UpButton />
                      </Box>
                    </>
                  )}
                </>
              )}
            </>
          )}
          {incomplete && (
            <>
              <DashboardContainer>
                {incompleteElements?.map((element, ind) => (
                  <DashboardIcon
                    key={element.id}
                    page={ind + 1}
                    setPage={setPage}
                    sampleId={sampleData.indexOf(element) + 1}
                    alert
                  />
                ))}
                {minElements && (
                  <StyledTooltip title="máximo de elementos" placement="top">
                    <MissingBox>
                      {minElements}
                      {IconAddMS}
                    </MissingBox>
                  </StyledTooltip>
                )}
              </DashboardContainer>
              {incompleteElements.length > 0 && (
                <>
                  {view === 'card' ? (
                    <>
                      <CardPagination
                        sampleCreationPage
                        sampleLength={incompleteElements.length}
                        page={page}
                        setPage={setPage}
                      />
                      <ElementCard
                        sampleData={sampleData}
                        setSampleData={setSampleData}
                        updateSample={updateSample}
                        disapprovedElements={disapprovedElements}
                        setDisapprovedElements={setDisapprovedElements}
                        sampleId={pageValue()}
                        elementData={incompleteElements[page - 1]}
                        propertyData={propertyData}
                        handleLastEditedPage={handleLastEditedPage}
                        page={page}
                        setPage={setPage}
                        deactivateFilters={deactivateFilters}
                        goToLastPage={
                          incompleteElements.length > 1 &&
                          incompleteElements.length === page
                        }
                      />
                    </>
                  ) : (
                    <>
                      <Box>
                        {incompleteElements.map((element, idx) => (
                          <Box id={`${idx + 1}`} key={element.id}>
                            <ElementCard
                              sampleData={sampleData}
                              setSampleData={setSampleData}
                              updateSample={updateSample}
                              disapprovedElements={disapprovedElements}
                              setDisapprovedElements={setDisapprovedElements}
                              sampleId={pageValue(element)}
                              elementData={element}
                              propertyData={propertyData}
                              handleLastEditedPage={handleLastEditedPage}
                              page={page}
                              setPage={setPage}
                              deactivateFilters={deactivateFilters}
                              goToLastPage={sampleData.length > 1}
                            />
                          </Box>
                        ))}
                        <UpButton />
                      </Box>
                    </>
                  )}
                </>
              )}
            </>
          )}
        </>
      )}
    </>
  );
}
